/**
 * What the mobile apps have reported failing, read from the product API.
 *
 * This is a counter, not a crash reporter. The ingest schema is a closed,
 * privacy-bounded allowlist: a `crash` event carries an app, a platform, a
 * release, an update channel and an update id, and nothing else. There is no
 * message, no exception type and no stack frame, and there never will be from
 * this endpoint. The screen is therefore built to answer *how often* and
 * *where* - which release, which OTA update, which app - because that is
 * enough to decide to pull an update, and it deliberately shows no column
 * that would only ever render empty.
 *
 * Every filter defaults to "All". The endpoint windows to seven days, which
 * is also the queue's retention, so a wider window is not offered: it could
 * only ever return a partial answer.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  correlationApiV1InternalObservabilityCorrelationsCorrelationIdGet,
  mobileTelemetryApiV1InternalObservabilityMobileTelemetryGet,
  type CorrelationResponse,
  type MobileApp,
  type MobilePlatform,
  type MobileSignal,
  type MobileSignalCount,
  type MobileTelemetryObservedEvent,
  type MobileTelemetryReport,
} from '@closet-theory/api';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, Code, Section, StatRow, StatTile } from '@ui/console/primitives';
import { MetricBand } from '@ui/console/MetricBand';
import { Term } from '@ui/console/glossary';
import { Quantity, formatCount, formatDuration, formatPercent, plural } from '@ui/console/measures';
import { RecordTable } from './record-table';
import { IdentifierCopy } from './references';
import { SelectField } from '@ui/console/forms';
import { InsightState, useInsight } from './consumer-insights';
import { Ranked, Timeline, bucketize } from './charts';
import { Actions, Filters, Page } from './components';

/** Upstream `MobileApp`. Admin has never reported an event; see `emptyRead`. */
const APPS: readonly MobileApp[] = ['consumer', 'stylist', 'admin'];
const PLATFORMS: readonly MobilePlatform[] = ['ios', 'android', 'web'];
const SIGNALS: readonly MobileSignal[] = [
  'app_started',
  'app_foregrounded',
  'app_backgrounded',
  'crash',
  'network_failure',
  'slow_render',
  'update_downloaded',
  'update_applied',
  'update_failed',
  'update_rollback',
  'hang',
  'startup_stall',
  'onboarding_checkpoint',
  'navigation',
  'replay_gap',
  'replay_checkpoint',
];

export type Severity = 'error' | 'degraded' | 'informational';

/**
 * Derived here, not sent by the endpoint: the payload has no severity field,
 * only a signal name. Without this an operator cannot tell a crash from a
 * navigation at a glance, because both render as an unrecognised state.
 */
const SEVERITY: Record<MobileSignal, Severity> = {
  crash: 'error',
  hang: 'error',
  startup_stall: 'error',
  network_failure: 'error',
  update_failed: 'error',
  update_rollback: 'error',
  slow_render: 'degraded',
  replay_gap: 'degraded',
  app_started: 'informational',
  app_foregrounded: 'informational',
  app_backgrounded: 'informational',
  update_downloaded: 'informational',
  update_applied: 'informational',
  onboarding_checkpoint: 'informational',
  navigation: 'informational',
  replay_checkpoint: 'informational',
};

export function severityOf(event: string): Severity {
  return SEVERITY[event as MobileSignal] ?? 'informational';
}

/** `limit` is `ge=1, le=200` and `page` is `ge=1, le=100` upstream. */
const PAGE_SIZES = ['25', '50', '100', '200'] as const;
const MAX_PAGE = 100;

/** The endpoint retains seven days, so nothing longer is offered. */
export const WINDOWS = [
  { value: 'all', label: 'All (the full seven days retained)' },
  { value: '24', label: 'Last 24 hours' },
  { value: '1', label: 'Last hour' },
] as const;

export type TelemetryFilters = {
  app: string;
  platform: string;
  event: string;
  severity: string;
  release: string;
  channel: string;
  window: string;
  limit: string;
  page: string;
};

/** Everything, explicitly. A screen that narrows itself by default lies. */
export const ALL_REPORTS: TelemetryFilters = {
  app: 'all',
  platform: 'all',
  event: 'all',
  severity: 'all',
  release: 'all',
  channel: 'all',
  window: 'all',
  limit: '50',
  page: '1',
};

/** What an event with no `update_channel` is called in a column or a filter. */
export const NO_CHANNEL = 'no channel';

export function channelOf(event: { update_channel?: string | null }): string {
  return event.update_channel ?? NO_CHANNEL;
}

export type TelemetryQuery = {
  app?: MobileApp;
  platform?: MobilePlatform;
  event?: MobileSignal;
  release?: string;
  start?: string;
  page: number;
  limit: number;
};

/**
 * Only the endpoint's own parameters are sent. `severity` is absent upstream,
 * so it is applied to the loaded page instead and is labelled as such.
 */
export function telemetryQuery(filters: TelemetryFilters, now = Date.now()): TelemetryQuery {
  const query: TelemetryQuery = {
    page: Number(filters.page) || 1,
    limit: Number(filters.limit) || 50,
  };
  if (filters.app !== 'all') query.app = filters.app as MobileApp;
  if (filters.platform !== 'all') query.platform = filters.platform as MobilePlatform;
  if (filters.event !== 'all') query.event = filters.event as MobileSignal;
  if (filters.release !== 'all') query.release = filters.release;
  if (filters.window !== 'all') {
    query.start = new Date(now - Number(filters.window) * 3_600_000).toISOString();
  }
  return query;
}

/** Which filters are actually narrowing, so an empty read can say why. */
export function narrowedBy(filters: TelemetryFilters): string[] {
  const named: Array<[keyof TelemetryFilters, string]> = [
    ['app', 'app'],
    ['platform', 'platform'],
    ['event', 'event type'],
    ['severity', 'severity'],
    ['release', 'release'],
    ['channel', 'update channel'],
  ];
  const narrowed = named
    .filter(([key]) => filters[key] !== 'all')
    .map(([key, label]) => `${label} ${filters[key]}`);
  if (filters.window !== 'all') narrowed.push(`the last ${filters.window} hour(s)`);
  return narrowed;
}

/**
 * An absence has to name what is absent. A filtered-to-nothing read and a
 * pipeline that has never carried anything are different facts, and only one
 * of them is about the filters.
 */
export function emptyRead(filters: TelemetryFilters): { title: string; body: string } {
  if (filters.app === 'admin' || filters.app === 'stylist') {
    return {
      title: `The ${filters.app} app has reported nothing.`,
      body: `Only the consumer app currently sends mobile telemetry. Until a reporter is wired into ${filters.app}, an empty result here is its permanent state and says nothing about whether that app is failing.`,
    };
  }
  const narrowed = narrowedBy(filters);
  if (narrowed.length) {
    return {
      title: 'No reports match these filters in this window.',
      body: `Nothing was reported under ${narrowed.join(', ')}. Widen the filters back to All before concluding that nothing happened.`,
    };
  }
  return {
    title: 'Nothing has been reported in this window.',
    body: 'No mobile app reported any event over the seven days this endpoint retains. The read succeeded and returned an empty report — this screen is working.',
  };
}

/**
 * Severity and update channel are applied to the loaded page, not the query,
 * so an empty table under either says nothing about the pages not loaded.
 */
export function pageFilterEmpty(
  filters: TelemetryFilters,
  hasNextPage: boolean,
): { title: string; body: string } | undefined {
  const narrowed: string[] = [];
  if (filters.severity !== 'all') narrowed.push(`severity ${filters.severity}`);
  if (filters.channel !== 'all') narrowed.push(`update channel ${filters.channel}`);
  if (!narrowed.length) return undefined;
  return {
    title: 'No reports on this page match these filters.',
    body: `${narrowed.join(' and ')} only narrow the events on this page, not the whole window. ${
      hasNextPage
        ? 'Press Next to check the next page, or set them back to All.'
        : 'This is the last page, so nothing else in this window matches.'
    }`,
  };
}

function count(rows: MobileTelemetryReport['signal_counts'], event: MobileSignal): number {
  return rows.reduce((total, row) => (row.event === event ? total + row.occurrences : total), 0);
}

/** One row per OTA update carrying a crash: the reading that justifies a pull. */
export function crashesByUpdate(
  events: readonly MobileTelemetryObservedEvent[],
): Array<Record<string, unknown>> {
  const grouped = new Map<string, Record<string, unknown> & { crashes: number }>();
  for (const event of events) {
    if (event.event !== 'crash') continue;
    const key = [event.app, event.platform, event.release, channelOf(event), event.update_id].join(
      '|',
    );
    const existing = grouped.get(key);
    if (existing) {
      existing.crashes += 1;
      continue;
    }
    grouped.set(key, {
      app: event.app,
      platform: event.platform,
      release: event.release,
      app_version: event.app_version,
      update_channel: event.update_channel ?? null,
      update_id: event.update_id ?? null,
      crashes: 1,
    });
  }
  return [...grouped.values()].sort((left, right) => right.crashes - left.crashes);
}

/**
 * Our own internal builds report through the same pipeline as members'
 * devices, so `production` and `production-internal` sit in one count. A bare
 * crash total is ambiguous until it is split this way.
 */
export function crashesByChannel(
  events: readonly MobileTelemetryObservedEvent[],
): Array<{ channel: string; crashes: number }> {
  const counts = new Map<string, number>();
  for (const event of events) {
    if (event.event !== 'crash') continue;
    const channel = channelOf(event);
    counts.set(channel, (counts.get(channel) ?? 0) + 1);
  }
  return [...counts]
    .map(([channel, crashes]) => ({ channel, crashes }))
    .sort((left, right) => right.crashes - left.crashes);
}

/* ------------------------------------------------------------- crash rate */

export type ReleaseRate = {
  release: string;
  crashes: number;
  starts: number;
  /** Crashes per app start, or `undefined` when no start was reported. */
  rate?: number;
};

/**
 * Crashes measured against how often the app was opened, per release.
 *
 * A raw crash count ranks releases by how many people are on them. The release
 * everyone uses always looks worst and a genuinely broken release nobody has
 * yet is invisible, which is exactly backwards for the question being asked.
 * `app_started` is the closest denominator the payload carries - it counts app
 * launches, not sessions and not devices, and a single device relaunching after
 * each crash inflates both sides of the fraction. It is still the right
 * comparison between two releases in the same window, and it is labelled as
 * launches everywhere it appears rather than dressed up as a session rate.
 */
export function crashRateByRelease(counts: readonly MobileSignalCount[]): ReleaseRate[] {
  const rows = new Map<string, ReleaseRate>();
  for (const row of counts) {
    const existing = rows.get(row.release) ?? { release: row.release, crashes: 0, starts: 0 };
    if (row.event === 'crash') existing.crashes += row.occurrences;
    if (row.event === 'app_started') existing.starts += row.occurrences;
    rows.set(row.release, existing);
  }
  return [...rows.values()]
    .map((row) => (row.starts > 0 ? { ...row, rate: row.crashes / row.starts } : row))
    .filter((row) => row.crashes > 0 || row.starts > 0)
    .sort((left, right) => (right.rate ?? -1) - (left.rate ?? -1) || right.crashes - left.crashes);
}

/** Crashes per thousand launches reads better than a four-decimal fraction. */
export function formatRate(rate: number | undefined): string {
  if (rate === undefined) return 'no launches reported';
  return `${(rate * 1000).toFixed(rate * 1000 < 10 ? 2 : 0)} per 1,000 launches`;
}

export function describeChannelSplit(
  split: ReadonlyArray<{ channel: string; crashes: number }>,
): string {
  if (!split.length) return 'No crash on this page of events.';
  return split.map((row) => `${row.crashes} ${row.channel}`).join(' · ');
}

/* --------------------------------------------------- server-side trail */

function count_(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

/**
 * The one thing that turns a bare crash count into something you can
 * investigate.
 *
 * A reported event carries a `correlation_id`: the same identifier the backend
 * stamped on every log line, span and audit entry produced by the request the
 * app was making when it failed. The report itself has no message and no stack
 * and never will, but the server's side of that same moment is recorded in
 * full, and this joins the two. It is the difference between "the consumer app
 * crashed 40 times on 1.2.4" and knowing what it was asking us for when it did.
 */
function CorrelationTrail({ correlationId }: { correlationId: string }) {
  const source = useInsight<CorrelationResponse>(
    () =>
      correlationApiV1InternalObservabilityCorrelationsCorrelationIdGet({
        path: { correlation_id: correlationId },
      }),
    [correlationId],
    { live: false },
  );
  return (
    <InsightState source={source} subject="the server-side trail for this report">
      {(bundle) => {
        const logs = count_((bundle.logs as { entries?: unknown } | undefined)?.entries);
        const spans = count_(
          (bundle.trace as { spans?: unknown } | null | undefined)?.spans ?? undefined,
        );
        const activity = bundle.activity.length;
        if (!logs && !spans && !activity) {
          return (
            <Callout tone="neutral" title="Nothing on our side carries this identifier.">
              The read succeeded and found no log line, span or audit entry stamped with it. The
              trail is kept for 24 hours by default, so a crash older than that has aged out rather
              than never having had one. Its absence says nothing about whether the crash was real.
            </Callout>
          );
        }
        return (
          <div className="flex flex-col gap-3">
            <StatRow>
              <StatTile
                label="Log lines"
                value={<Quantity value={formatCount(logs)} unit="lines" raw={logs} />}
                hint="What the backend wrote while handling this request."
              />
              <StatTile
                label="Trace spans"
                value={<Quantity value={formatCount(spans)} unit="spans" raw={spans} />}
                hint="Each step the request took across our services, and how long each one held it."
              />
              <StatTile
                label="Audit entries"
                value={<Quantity value={formatCount(activity)} unit="entries" raw={activity} />}
                hint="Recorded changes made during this request."
              />
            </StatRow>
            <div className="flex flex-wrap gap-6 rounded-xl border border-secondary bg-primary p-4">
              <IdentifierCopy label="Correlation ID" value={bundle.correlation_id} />
              {bundle.request_id ? (
                <IdentifierCopy label="Request ID" value={bundle.request_id} />
              ) : null}
              {bundle.trace_id ? <IdentifierCopy label="Trace ID" value={bundle.trace_id} /> : null}
            </div>
            {bundle.activity.length ? (
              <RecordTable noun="audit entries" records={bundle.activity} />
            ) : null}
            {bundle.activity_error || bundle.durable_error ? (
              <Callout tone="warning" title="Part of the trail could not be read.">
                {[bundle.activity_error, bundle.durable_error].filter(Boolean).join(' · ')} What is
                shown above is therefore incomplete.
              </Callout>
            ) : null}
          </div>
        );
      }}
    </InsightState>
  );
}

export function MobileTelemetry() {
  // Every filter lives in the URL, so a view an operator wants a second opinion
  // on can simply be sent as a link.
  const [params, setParams] = useSearchParams();
  const filters: TelemetryFilters = {
    app: params.get('app') ?? ALL_REPORTS.app,
    platform: params.get('platform') ?? ALL_REPORTS.platform,
    event: params.get('event') ?? ALL_REPORTS.event,
    severity: params.get('severity') ?? ALL_REPORTS.severity,
    release: params.get('release') ?? ALL_REPORTS.release,
    channel: params.get('channel') ?? ALL_REPORTS.channel,
    window: params.get('window') ?? ALL_REPORTS.window,
    limit: params.get('limit') ?? ALL_REPORTS.limit,
    page: params.get('page') ?? ALL_REPORTS.page,
  };
  const [tracing, setTracing] = useState<string>();
  const query = telemetryQuery(filters);
  const serialized = JSON.stringify(query);
  const source = useInsight<MobileTelemetryReport>(
    () => mobileTelemetryApiV1InternalObservabilityMobileTelemetryGet({ query }),
    [serialized],
  );

  // Any filter change invalidates the page you were reading.
  const set = (key: keyof TelemetryFilters, value: string) => {
    const next: TelemetryFilters = {
      ...filters,
      [key]: value,
      ...(key === 'page' ? {} : { page: '1' }),
    };
    // Only what differs from "everything" is written, so a shared link stays
    // readable and an untouched filter is absent rather than spelled out.
    setParams(
      Object.fromEntries(
        Object.entries(next).filter(
          ([name, chosen]) => chosen !== ALL_REPORTS[name as keyof TelemetryFilters],
        ),
      ),
      { replace: true },
    );
  };

  // Built from the same read the content is, one level up, so the figures sit
  // above the filter strip rather than inside the table's own state.
  const report = source.state.status === 'ready' ? source.state.data : undefined;
  const bandCrashes = (report?.crashes_by_release ?? []).reduce((sum, row) => sum + row.crashes, 0);
  const bandFailures = report ? count(report.signal_counts, 'network_failure') : 0;
  const bandDropped = report?.queue.dropped_events ?? 0;
  const bandEvents = report?.total_events ?? 0;
  // The per-release breakdown stops at the busiest groups; the event and crash
  // totals are counted on their own and always cover the whole window.
  const partial = report?.signal_counts_truncated === true;

  return (
    <Page
      title="Mobile error reports"
      purpose="Use this page to find out how often the mobile apps are failing, which release and update it is happening on, and where in the app — enough to decide whether to pull an update. It cannot tell you why on its own: these reports carry no error message and no stack trace. What it can do is follow one report into what our own servers were doing at that same moment."
      band={
        <MetricBand
          label="How the apps are failing in this window"
          figures={[
            {
              id: 'events',
              label: 'Events reported',
              ...(report
                ? {
                    value: formatCount(bandEvents),
                    unit: plural(bandEvents, 'event'),
                    raw: bandEvents,
                  }
                : { absent: 'The telemetry read has not come back yet.' }),
              hint: 'Everything reported in this window, at every severity.',
              tooltip:
                'Counts are events rather than devices: one device in a crash loop and fifty devices crashing once read the same.',
            },
            {
              id: 'crashes',
              label: 'Crashes',
              ...(report
                ? {
                    value: formatCount(bandCrashes),
                    unit: plural(bandCrashes, 'crash', 'crashes'),
                    raw: bandCrashes,
                    ...(bandCrashes ? { tone: 'error' as const, statusLabel: 'crashing' } : {}),
                    ...(bandEvents > 0
                      ? {
                          bar: {
                            fraction: bandCrashes / bandEvents,
                            label: `Crashes as a share of all events: ${bandCrashes} of ${bandEvents}`,
                            tone: 'error' as const,
                          },
                        }
                      : {}),
                  }
                : { absent: 'The telemetry read has not come back yet.' }),
              hint: 'The app stopped. Which release and which update channel is below, and both change what this number means.',
            },
            {
              id: 'failures',
              label: 'Network failures',
              ...(report
                ? {
                    value: formatCount(bandFailures),
                    unit: plural(bandFailures, 'failure'),
                    raw: bandFailures,
                    ...(bandFailures
                      ? { tone: 'warning' as const, statusLabel: 'needs a look' }
                      : {}),
                    ...(bandEvents > 0
                      ? {
                          bar: {
                            fraction: bandFailures / bandEvents,
                            label: `Network failures as a share of all events: ${bandFailures} of ${bandEvents}`,
                            tone: 'warning' as const,
                          },
                        }
                      : {}),
                  }
                : { absent: 'The telemetry read has not come back yet.' }),
              hint: partial
                ? 'A call from the app to us that did not get through. Counted from the per-release breakdown, which is partial in this window, so this may be low.'
                : 'A call from the app to us that did not get through. Reported without a URL or a status code, so there is nothing here to say which call it was.',
            },
            {
              id: 'dropped',
              label: 'Dropped before we stored them, all time',
              ...(report
                ? {
                    value: formatCount(bandDropped),
                    unit: plural(bandDropped, 'report'),
                    raw: bandDropped,
                    ...(bandDropped
                      ? { tone: 'warning' as const, statusLabel: 'counts incomplete' }
                      : {}),
                  }
                : { absent: 'The telemetry read has not come back yet.' }),
              hint: report?.queue.under_pressure
                ? 'Counted over every report ever received, not this window. Ingest is shedding events now, so every other figure on this page is an undercount.'
                : 'Counted over every report ever received, not this window. Shed only once ingest passes its pressure threshold.',
            },
          ]}
        />
      }
      actions={
        <Button size="md" color="secondary" onClick={() => void source.refresh()}>
          Refresh
        </Button>
      }
    >
      <Callout
        tone="warning"
        title="The report itself never says why. Our side of it sometimes can"
      >
        The ingest schema is a closed, privacy-bounded allowlist: no message, no exception type, no
        stack frame, and for a <Code>network_failure</Code> no URL or status code. What a report
        does carry is where it happened — <Code>screen</Code> and <Code>component</Code> — and a{' '}
        <Term term="correlation id" />, which joins it to everything our backend logged while
        handling the same request. That is as close to a cause as this pipeline gets, and it is at
        the bottom of this page. Counts are events rather than devices, so one device in a crash
        loop and fifty devices crashing once read the same. Our own internal builds report through
        this same pipeline under <Code>production-internal</Code>, so read the update channel before
        reading a count. Nothing on this page auto-refreshes.
      </Callout>

      <InsightState source={source} subject="mobile error reports">
        {(report) => {
          const crashes = [...report.crashes_by_release].sort((a, b) => b.crashes - a.crashes);
          const reportingApps = [...new Set(report.signal_counts.map((row) => row.app))].sort();
          const releases = [
            ...new Set([
              ...crashes.map((row) => row.release),
              ...report.signal_counts.map((row) => row.release),
            ]),
          ].sort();
          const channels = [...new Set(report.events.map(channelOf))].sort();
          const channelSplit = crashesByChannel(report.events);
          const pageCrashes = channelSplit.reduce((sum, row) => sum + row.crashes, 0);
          const rows = report.events
            .map((event) => ({
              ...event,
              severity: severityOf(event.event),
              update_channel: channelOf(event),
            }))
            .filter((event) => filters.severity === 'all' || event.severity === filters.severity)
            .filter(
              (event) => filters.channel === 'all' || event.update_channel === filters.channel,
            );
          const absence = emptyRead(filters);
          const hasNextPage = report.events.length >= Number(filters.limit);
          const pageAbsence =
            report.events.length > 0 ? pageFilterEmpty(filters, hasNextPage) : undefined;
          const oldestPending = report.queue.oldest_pending_age_seconds;
          const rates = crashRateByRelease(report.signal_counts);
          const errorEvents = report.events.filter(
            (item) => severityOf(item.event) !== 'informational',
          );
          const errorBuckets = bucketize(
            errorEvents,
            (item) => new Date(item.observed_at).getTime(),
            (item) => item.event,
            18,
          );
          const rank = (key: (item: MobileTelemetryObservedEvent) => string | null | undefined) => {
            const counts = new Map<string, number>();
            for (const item of errorEvents) {
              const value = key(item);
              if (!value) continue;
              counts.set(value, (counts.get(value) ?? 0) + 1);
            }
            return [...counts].map(([id, count]) => ({
              id,
              label: id,
              count,
              tone: 'error' as const,
            }));
          };
          const screenRows = rank((item) => item.screen);
          const componentRows = rank((item) => item.component);

          return (
            <div className="flex flex-col gap-6">
              {report.signal_counts_truncated ? (
                <Callout tone="warning" title="The per-release breakdown is partial">
                  This window has more app, release and signal combinations than the breakdown
                  lists, so it shows the busiest ones only. Events reported and Crashes in the band
                  above are complete. The release tables and Network failures cover only what is
                  listed.
                </Callout>
              ) : null}
              <StatRow>
                <StatTile
                  label="Crashes by update channel"
                  value={
                    <Quantity
                      value={formatCount(pageCrashes)}
                      unit={plural(pageCrashes, 'crash', 'crashes')}
                      raw={pageCrashes}
                    />
                  }
                  {...(pageCrashes ? { tone: 'error' as const, statusLabel: 'crashing' } : {})}
                  hint={`${describeChannelSplit(channelSplit)}. Our own internal builds report through this same pipeline, so a total that does not name its channel is ambiguous. This one counts the events on the page below.`}
                />
                <StatTile
                  label="Apps reporting"
                  value={reportingApps.length ? reportingApps.join(', ') : 'none'}
                  hint="Only apps that sent something in this window appear."
                />
                <StatTile
                  label="Oldest report waiting to be stored"
                  value={
                    oldestPending === null || oldestPending === undefined
                      ? 'none waiting'
                      : formatDuration(oldestPending)
                  }
                  {...(oldestPending !== null && oldestPending !== undefined && oldestPending > 3600
                    ? { tone: 'warning' as const, statusLabel: 'backlog' }
                    : {})}
                  hint="How long the oldest batch of reports has been waiting to be stored, across all time rather than this window. A figure that keeps growing means ingest is stuck."
                />
              </StatRow>

              <Section
                title="Which release is worst"
                description="Crashes measured against how often each release was opened, so a release everyone is on does not look worse than one that is genuinely broken. Counted over the whole window."
              >
                {rates.length ? (
                  <RecordTable
                    noun="releases"
                    records={rates.map((row) => ({
                      release: row.release,
                      crash_rate: formatRate(row.rate),
                      crashes: row.crashes,
                      app_launches: row.starts,
                      share_of_launches_crashing:
                        row.rate === undefined ? '—' : formatPercent(row.rate, 2),
                    }))}
                    columns={[
                      'release',
                      'crash_rate',
                      'crashes',
                      'app_launches',
                      'share_of_launches_crashing',
                    ]}
                  />
                ) : (
                  <Callout tone="neutral" title="No crash was reported in this window.">
                    Nothing reported a crash. That is a statement about what reached the network,
                    not about every device.
                  </Callout>
                )}
                <p className="text-xs text-tertiary">
                  The denominator is app launches, the one count the report carries that scales with
                  use. It is not sessions and not devices: a device that relaunches after every
                  crash adds to both sides. Nothing here counts devices, so one phone in a crash
                  loop and fifty phones crashing once read identically.
                </p>
              </Section>

              <Section
                title="Errors over time"
                description="Every crash, hang, stall and failed update on the page of events below, across the span they cover. A period with nothing reported is left empty rather than closed up."
              >
                {errorBuckets.length ? (
                  <Timeline
                    buckets={errorBuckets}
                    series={[
                      { id: 'crash', label: 'crash', tone: 'error' },
                      { id: 'hang', label: 'hang', tone: 'error' },
                      { id: 'startup_stall', label: 'startup stall', tone: 'error' },
                      { id: 'network_failure', label: 'network failure', tone: 'warning' },
                      { id: 'update_failed', label: 'update failed', tone: 'warning' },
                      { id: 'update_rollback', label: 'update rolled back', tone: 'warning' },
                      { id: 'slow_render', label: 'slow render', tone: 'neutral' },
                    ]}
                    noun="error events"
                    caption="Covers this page of events only, not the whole window. Load a larger page to widen it."
                  />
                ) : (
                  <Callout tone="neutral" title="No error appears on this page of events.">
                    There is nothing to plot. This is an empty page of results, not a failed read.
                  </Callout>
                )}
              </Section>

              <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
                <Section
                  title="Where in the app it failed"
                  description="The screen the app was on when it reported an error. Grouped from the events below, so it covers this page only."
                >
                  <Ranked
                    rows={screenRows}
                    total={screenRows.reduce((sum, row) => sum + row.count, 0)}
                    unit="errors"
                    emptyHint="No error on this page names a screen. The field is optional and the app does not always fill it in, so this is not a statement that errors happen nowhere."
                  />
                </Section>

                <Section
                  title="Which component reported it"
                  description="The part of the app that raised the report, where it named one. Also this page only."
                >
                  <Ranked
                    rows={componentRows}
                    total={componentRows.reduce((sum, row) => sum + row.count, 0)}
                    unit="errors"
                    emptyHint="No error on this page names a component."
                  />
                </Section>
              </div>

              <Section
                title="Crashes by release, as raw counts"
                description="The endpoint's own rollup over the whole window. It does not split by update channel, so an internal build sharing a release name is counted here too."
              >
                {crashes.length ? (
                  <RecordTable
                    noun="crashing releases"
                    records={crashes}
                    columns={['release', 'crashes']}
                  />
                ) : (
                  <Callout tone="neutral" title="No crash was reported in this window.">
                    Nothing reported a crash.
                  </Callout>
                )}
              </Section>

              <Section
                title={
                  <>
                    Crashes by <Term term="OTA" /> update
                  </>
                }
                description="Grouped from the events listed below, so it covers this page only. The update id and the release together are what you need in order to pull an update."
              >
                <RecordTable
                  noun="crashing updates"
                  records={crashesByUpdate(report.events)}
                  columns={[
                    'update_channel',
                    'release',
                    'update_id',
                    'app',
                    'platform',
                    'app_version',
                    'crashes',
                  ]}
                  emptyHint="No crash appears on this page of events."
                />
              </Section>

              <Section
                title="Signals by app and release"
                description="Occurrences per app, platform, release and signal over the whole window."
              >
                <RecordTable
                  noun="signal groups"
                  records={report.signal_counts.map((row) => ({
                    ...row,
                    severity: severityOf(row.event),
                  }))}
                  columns={['severity', 'event', 'app', 'platform', 'release', 'occurrences']}
                  emptyHint={absence.body}
                />
              </Section>

              <Section
                title="Reported events"
                description="Individual reports. Every filter starts at All; nothing here is narrowed for you."
              >
                <Filters>
                  <SelectField
                    label="App"
                    value={filters.app}
                    onChange={(event) => set('app', event.target.value)}
                    options={[
                      { value: 'all', label: 'All' },
                      ...APPS.map((app) => ({ value: app, label: app })),
                    ]}
                    hint="Which app reported. Only consumer sends today."
                  />
                  <SelectField
                    label="Platform"
                    value={filters.platform}
                    onChange={(event) => set('platform', event.target.value)}
                    options={[
                      { value: 'all', label: 'All' },
                      ...PLATFORMS.map((platform) => ({ value: platform, label: platform })),
                    ]}
                  />
                  <SelectField
                    label="Severity"
                    value={filters.severity}
                    onChange={(event) => set('severity', event.target.value)}
                    options={[
                      { value: 'all', label: 'All' },
                      { value: 'error', label: 'Errors and crashes' },
                      { value: 'degraded', label: 'Degraded' },
                      { value: 'informational', label: 'Informational' },
                    ]}
                    hint="Derived from the signal name, so it narrows this page rather than the query."
                  />
                  <SelectField
                    label="Event type"
                    value={filters.event}
                    onChange={(event) => set('event', event.target.value)}
                    options={[
                      { value: 'all', label: 'All' },
                      ...SIGNALS.map((signal) => ({ value: signal, label: signal })),
                    ]}
                  />
                  <SelectField
                    label="Release"
                    value={filters.release}
                    onChange={(event) => set('release', event.target.value)}
                    options={[
                      { value: 'all', label: 'All' },
                      ...releases.map((release) => ({ value: release, label: release })),
                    ]}
                    hint="Releases seen in this window."
                  />
                  <SelectField
                    label="Update channel"
                    value={filters.channel}
                    onChange={(event) => set('channel', event.target.value)}
                    options={[
                      { value: 'all', label: 'All' },
                      ...channels.map((channel) => ({ value: channel, label: channel })),
                    ]}
                    hint="production-internal is us, not members. Only channels seen on this page are offered, and it narrows this page rather than the query."
                  />
                  <SelectField
                    label="Window"
                    value={filters.window}
                    onChange={(event) => set('window', event.target.value)}
                    options={WINDOWS.map((window) => ({ ...window }))}
                    hint="Seven days is all the endpoint retains."
                  />
                  <SelectField
                    label="Events per page"
                    value={filters.limit}
                    onChange={(event) => set('limit', event.target.value)}
                    options={[...PAGE_SIZES]}
                  />
                </Filters>

                {rows.length ? (
                  <RecordTable
                    noun="reported events"
                    records={rows}
                    columns={[
                      'observed_at',
                      'severity',
                      'event',
                      'screen',
                      'component',
                      'failure_code',
                      'duration_bucket',
                      'app',
                      'platform',
                      'update_channel',
                      'release',
                      'app_version',
                      'runtime_version',
                      'update_id',
                      'authenticated',
                      'correlation_id',
                    ]}
                    rowActions={(record) =>
                      typeof record.correlation_id === 'string' && record.correlation_id ? (
                        <Button
                          size="sm"
                          color="secondary"
                          onClick={() => setTracing(record.correlation_id as string)}
                        >
                          Trace on our side
                        </Button>
                      ) : null
                    }
                  />
                ) : (
                  <Callout tone="neutral" title={(pageAbsence ?? absence).title}>
                    {(pageAbsence ?? absence).body}
                  </Callout>
                )}

                <Actions>
                  <span className="mr-auto text-sm text-tertiary">Page {filters.page}</span>
                  <Button
                    size="sm"
                    color="secondary"
                    isDisabled={filters.page === '1'}
                    onClick={() => set('page', String(Math.max(1, Number(filters.page) - 1)))}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    color="secondary"
                    isDisabled={!hasNextPage}
                    onClick={() =>
                      set('page', String(Math.min(MAX_PAGE, Number(filters.page) + 1)))
                    }
                  >
                    Next
                  </Button>
                </Actions>
              </Section>

              <Section
                title="What our side was doing"
                description="A report carries no message and no stack, but it does carry the identifier the backend stamped on everything it logged while handling the same request. This is that trail."
                {...(tracing
                  ? {
                      actions: (
                        <Button size="sm" color="secondary" onClick={() => setTracing(undefined)}>
                          Close
                        </Button>
                      ),
                    }
                  : {})}
              >
                {tracing ? (
                  <CorrelationTrail correlationId={tracing} />
                ) : (
                  <Callout tone="neutral" title="Pick a report to follow it into the backend.">
                    Use <Code>Trace on our side</Code> on any row above that carries a{' '}
                    <Term term="correlation id" />. Not every report has one: an app that crashed
                    without a request in flight has nothing on our side to join to, and that is a
                    real answer rather than a missing field.
                  </Callout>
                )}
              </Section>
            </div>
          );
        }}
      </InsightState>
    </Page>
  );
}
